import { FC, useEffect, useRef } from "react";

type Landmark = {
	x: number;
	y: number;
	sigma: number;
	weight: number;
};

/**
 * Pressure landmarks in "foot space": the foot is one unit long, y runs from 0 at
 * the toes to 1 at the heel and x is centred on the middle of the foot. The
 * contour map is the iso-lines of the gaussian field these landmarks generate.
 */
const LANDMARKS: Landmark[] = [
	// Toes.
	{ x: -0.11, y: 0.09, sigma: 0.043, weight: 0.85 },
	{ x: -0.026, y: 0.033, sigma: 0.04, weight: 0.85 },
	{ x: 0.052, y: 0.05, sigma: 0.034, weight: 0.8 },
	{ x: 0.119, y: 0.088, sigma: 0.031, weight: 0.78 },
	{ x: 0.176, y: 0.136, sigma: 0.028, weight: 0.75 },
	// Ball, arch and heel.
	{ x: 0.0, y: 0.279, sigma: 0.068, weight: 1.0 },
	{ x: -0.007, y: 0.424, sigma: 0.059, weight: 0.92 },
	{ x: -0.031, y: 0.6, sigma: 0.053, weight: 0.88 },
	{ x: -0.026, y: 0.767, sigma: 0.056, weight: 0.9 },
	{ x: 0.0, y: 0.948, sigma: 0.068, weight: 1.0 },
	// Broad, faint masses that pull the low contours into one foot-shaped
	// outline instead of a string of separate bulges.
	{ x: 0.02, y: 0.2, sigma: 0.15, weight: 0.16 },
	{ x: -0.02, y: 0.45, sigma: 0.11, weight: 0.13 },
	{ x: -0.01, y: 0.7, sigma: 0.12, weight: 0.14 },
	{ x: 0.0, y: 0.9, sigma: 0.13, weight: 0.15 }
];

const GRID_STEP = 3; // px between field samples
const LEVEL_COUNT = 30;
const HIGHEST_LEVEL = 1.15;
/**
 * Each iso-level is a fixed fraction of the one before it. The field falls off
 * as 1/distance², so radius scales with 1/sqrt(level) and a constant ratio puts
 * the rings on a geometric progression: tight around the landmarks, opening out
 * into the wide arcs that cross the panel.
 */
const LEVEL_RATIO = 0.845;
const SWEEP_DURATION = 9000; // ms for one full pass down the panel

type Contours = {
	/** One path per iso-level, outermost first, so the base pass can ramp opacity. */
	levels: Path2D[];
	/** Every level flattened into a single path, for the cheap per-frame scan pass. */
	combined: Path2D;
};

/**
 * Inverse-quadratic metaball field. Gaussians would merge the landmarks just as
 * nicely but their tails vanish within a few sigma, so the wide contour arcs
 * that sweep across the panel would have nothing to trace. This falls off as
 * 1/distance² instead, which keeps the far field alive.
 */
const sampleField = (x: number, y: number): number => {
	let sum = 0;
	for (let index = 0; index < LANDMARKS.length; index++) {
		const point = LANDMARKS[index];
		const dx = x - point.x;
		const dy = y - point.y;
		const spread = point.sigma * point.sigma;
		sum += (point.weight * spread) / (dx * dx + dy * dy + spread);
	}
	return sum;
};

const buildContours = (width: number, height: number): Contours => {
	const cols = Math.ceil(width / GRID_STEP) + 1;
	const rows = Math.ceil(height / GRID_STEP) + 1;

	// The foot sits slightly left of centre and takes up a little over half the height.
	const scale = height * 0.56;
	const originX = width * 0.5;
	const originY = height * 0.5 - scale * 0.5;

	const field = new Float32Array(cols * rows);
	for (let row = 0; row < rows; row++) {
		const footY = (row * GRID_STEP - originY) / scale;
		for (let col = 0; col < cols; col++) {
			const footX = (col * GRID_STEP - originX) / scale;
			field[row * cols + col] = sampleField(footX, footY);
		}
	}

	const levels: Path2D[] = [];
	const combined = new Path2D();

	// Highest level first: the tight rings hugging each landmark, widening outwards.
	for (let step = 0; step < LEVEL_COUNT; step++) {
		const level = HIGHEST_LEVEL * Math.pow(LEVEL_RATIO, step);
		const path = new Path2D();

		for (let row = 0; row < rows - 1; row++) {
			for (let col = 0; col < cols - 1; col++) {
				const topLeft = field[row * cols + col];
				const topRight = field[row * cols + col + 1];
				const bottomRight = field[(row + 1) * cols + col + 1];
				const bottomLeft = field[(row + 1) * cols + col];

				// Marching squares: one bit per corner that sits above the iso-level.
				const marker =
					(topLeft > level ? 8 : 0) |
					(topRight > level ? 4 : 0) |
					(bottomRight > level ? 2 : 0) |
					(bottomLeft > level ? 1 : 0);

				if (marker === 0 || marker === 15) {
					continue;
				}

				const left = col * GRID_STEP;
				const top = row * GRID_STEP;
				const right = left + GRID_STEP;
				const bottom = top + GRID_STEP;

				// Where each edge crosses the iso-level, by linear interpolation.
				const topPoint = [left + (GRID_STEP * (level - topLeft)) / (topRight - topLeft), top];
				const rightPoint = [right, top + (GRID_STEP * (level - topRight)) / (bottomRight - topRight)];
				const bottomPoint = [left + (GRID_STEP * (level - bottomLeft)) / (bottomRight - bottomLeft), bottom];
				const leftPoint = [left, top + (GRID_STEP * (level - topLeft)) / (bottomLeft - topLeft)];

				const addSegment = (from: number[], to: number[]) => {
					path.moveTo(from[0], from[1]);
					path.lineTo(to[0], to[1]);
					combined.moveTo(from[0], from[1]);
					combined.lineTo(to[0], to[1]);
				};

				switch (marker) {
					case 1:
					case 14:
						addSegment(leftPoint, bottomPoint);
						break;
					case 2:
					case 13:
						addSegment(bottomPoint, rightPoint);
						break;
					case 3:
					case 12:
						addSegment(leftPoint, rightPoint);
						break;
					case 4:
					case 11:
						addSegment(topPoint, rightPoint);
						break;
					case 6:
					case 9:
						addSegment(topPoint, bottomPoint);
						break;
					case 7:
					case 8:
						addSegment(leftPoint, topPoint);
						break;
					case 5:
						addSegment(leftPoint, topPoint);
						addSegment(bottomPoint, rightPoint);
						break;
					case 10:
						addSegment(leftPoint, bottomPoint);
						addSegment(topPoint, rightPoint);
						break;
					default:
						break;
				}
			}
		}

		levels.push(path);
	}

	return { levels, combined };
};

type Palette = {
	backgroundTop: string;
	backgroundBottom: string;
	line: string;
	scan: string;
};

const readPalette = (element: HTMLElement): Palette => {
	const styles = getComputedStyle(element);
	const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;
	return {
		backgroundTop: read("--cf-art-bg-top", "#12303a"),
		backgroundBottom: read("--cf-art-bg-bottom", "#0a1c23"),
		line: read("--cf-art-line", "rgba(150, 205, 220, 0.5)"),
		scan: read("--cf-art-scan", "rgba(224, 164, 74, 1)")
	};
};

type FootTopographyProps = {
	/** Only used to re-read the palette when the theme flips. */
	scheme: string;
};

const FootTopography: FC<FootTopographyProps> = ({ scheme }): JSX.Element => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const container = canvas?.parentElement;
		if (!canvas || !container) {
			return;
		}
		const context = canvas.getContext("2d");
		if (!context) {
			return;
		}

		const reduceMotion =
			typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		let frame = 0;
		let width = 0;
		let height = 0;
		let contours: Contours | null = null;
		let base: HTMLCanvasElement | null = null;
		let palette = readPalette(container);

		const drawBase = () => {
			if (!contours || width === 0 || height === 0) {
				return;
			}
			const ratio = Math.min(window.devicePixelRatio || 1, 2);
			base = document.createElement("canvas");
			base.width = Math.round(width * ratio);
			base.height = Math.round(height * ratio);
			const baseContext = base.getContext("2d");
			if (!baseContext) {
				return;
			}
			baseContext.scale(ratio, ratio);

			const backdrop = baseContext.createLinearGradient(0, 0, width * 0.35, height);
			backdrop.addColorStop(0, palette.backgroundTop);
			backdrop.addColorStop(1, palette.backgroundBottom);
			baseContext.fillStyle = backdrop;
			baseContext.fillRect(0, 0, width, height);

			baseContext.lineWidth = 1;
			baseContext.strokeStyle = palette.line;
			contours.levels.forEach((path, index) => {
				// Firm on the rings hugging a landmark, fading out towards the wide arcs.
				const distance = index / (LEVEL_COUNT - 1);
				baseContext.globalAlpha = 1 - 0.88 * distance * distance;
				baseContext.stroke(path);
			});
			baseContext.globalAlpha = 1;

			// Settle the outermost arcs back into the background at the panel edges.
			const vignette = baseContext.createRadialGradient(
				width * 0.5,
				height * 0.5,
				Math.min(width, height) * 0.2,
				width * 0.5,
				height * 0.5,
				Math.max(width, height) * 0.75
			);
			vignette.addColorStop(0, "transparent");
			vignette.addColorStop(1, palette.backgroundBottom);
			baseContext.globalAlpha = 0.85;
			baseContext.fillStyle = vignette;
			baseContext.fillRect(0, 0, width, height);
			baseContext.globalAlpha = 1;
		};

		const render = (timestamp: number) => {
			if (!base || !contours) {
				return;
			}
			context.clearRect(0, 0, width, height);
			context.drawImage(base, 0, 0, width, height);

			const scanned = contours.combined;

			if (!reduceMotion) {
				const progress = (timestamp % SWEEP_DURATION) / SWEEP_DURATION;
				const scanY = -height * 0.1 + progress * height * 1.2;

				// The scan re-lights nearby contours: three nested bands fake a soft falloff.
				context.save();
				context.lineWidth = 1;
				context.strokeStyle = palette.scan;
				[
					{ half: 14, alpha: 0.95 },
					{ half: 46, alpha: 0.4 },
					{ half: 110, alpha: 0.14 }
				]
					.reverse()
					.forEach(({ half, alpha }) => {
						context.save();
						context.beginPath();
						context.rect(0, scanY - half, width, half * 2);
						context.clip();
						context.globalAlpha = alpha;
						context.stroke(scanned);
						context.restore();
					});
				context.restore();

				const halo = context.createLinearGradient(0, scanY - 90, 0, scanY + 90);
				halo.addColorStop(0, "transparent");
				halo.addColorStop(0.5, palette.scan);
				halo.addColorStop(1, "transparent");
				context.save();
				context.globalAlpha = 0.12;
				context.fillStyle = halo;
				context.fillRect(0, scanY - 90, width, 180);
				context.globalAlpha = 0.55;
				context.fillStyle = palette.scan;
				context.fillRect(0, scanY - 0.5, width, 1);
				context.restore();
			}

			frame = window.requestAnimationFrame(render);
		};

		const resize = () => {
			const bounds = container.getBoundingClientRect();
			if (bounds.width === 0 || bounds.height === 0) {
				return;
			}
			// Rebuilding the field is expensive, so ignore sub-pixel resize noise.
			if (contours && Math.abs(bounds.width - width) < 2 && Math.abs(bounds.height - height) < 2) {
				return;
			}
			width = bounds.width;
			height = bounds.height;
			const ratio = Math.min(window.devicePixelRatio || 1, 2);
			canvas.width = Math.round(width * ratio);
			canvas.height = Math.round(height * ratio);
			canvas.style.width = `${width}px`;
			canvas.style.height = `${height}px`;
			context.setTransform(ratio, 0, 0, ratio, 0, 0);

			palette = readPalette(container);
			contours = buildContours(width, height);
			drawBase();

			window.cancelAnimationFrame(frame);
			if (reduceMotion) {
				if (base) {
					context.clearRect(0, 0, width, height);
					context.drawImage(base, 0, 0, width, height);
				}
			} else {
				frame = window.requestAnimationFrame(render);
			}
		};

		resize();
		const observer = new ResizeObserver(resize);
		observer.observe(container);

		return () => {
			observer.disconnect();
			window.cancelAnimationFrame(frame);
		};
	}, [scheme]);

	return <canvas ref={canvasRef} className="cf-art__canvas" aria-hidden="true" />;
};

export default FootTopography;
